<template>
    <div class="p-fluid p-grid px-2">
        <div class="p-col-6 ">
            <h4 class="Paragraph-Medium" >2 - Step Verification</h4>
        </div>
        <div class="p-col-6 ">
            <div class="border-round-sm mt-3">
                <Button label="Disable" size="small" :class="EnableBtn" v-if="isActive"
                    @click="sendVerificationCode(false)" :loading="resendingCode" />
                <Button label="Enable" size="small" :class="EnableBtn" v-else @click="sendVerificationCode(false)"
                    :loading="resendingCode" />
            </div>
        </div>
    </div>

    <ConfirmDialog group="verify_2fa">
        <template #container="{ message, rejectCallback }">
            <div class="p-4 text-center justify-content-center">
                <h2 class="font-bold text-lg mb-3">{{ message.header }}</h2>
                <p>We've sent a verification code to <br /> <b>{{ props.profile.email }}</b>.</p>
                <p>Please enter it here.</p>
                <div class="card flex justify-center">
                    <Divider />
                    <InputOtp v-model="otp" variant="filled" :length="6" />
                    <Divider />
                </div>
                <div class="mt-6 flex justify-center gap-4 justify-content-center">
                    <Button label="Verify" @click="verifyCode" class="teal-button" size="small"
                        :disabled="otp.length < 6" />
                    <Button label="Cancel" @click="rejectCallback" severity="danger" variant="text" size="small" />
                </div>
                <!-- <div class="mt-1 flex justify-center gap-4 justify-content-center">
                    <Button label="Cancel" @click="rejectCallback" severity="danger" variant="text" size="small" />
                </div> -->
                <Divider />
                <div class="flex justify-center gap-4 mt-2 justify-content-center">
                    <Button label="Resend Code" @click="sendVerificationCode(true)" severity="secondary" variant="text"
                        size="small" :loading="resendingCode" />
                </div>
                <div class="flex justify-center gap-4 mt-2 justify-content-center">
                    <Button label="I don't have access to this email" @click="rejectCallback" severity="contrast"
                        variant="text" size="small" />
                </div>
            </div>
        </template>
    </ConfirmDialog>

    <ConfirmDialog group="register_totp">
        <template #container="{ }">
            <Panel class="text-center p-6">
                <div class="font-bold text-xl mb-2">Setup two-factor authentication</div>
                <p class="text-surface-500 dark:text-surface-400 block mb-4">
                    Use Google Authenticator or similar app to scan the QR code
                </p>

                <div class="card flex justify-center justify-content-center">
                    <Image :src="qrCodeUrl" alt="QR Code" width="250" preview v-if="qrCodeUrl" />
                    <Skeleton size="5rem" v-else></Skeleton>
                </div>

                <div class="flex justify-content-center mt-5">
                    <Button label="Finish" :class="EnableBtn" @click="activateTwoStep" :disabled="!qrCodeUrl" />
                </div>
            </Panel>
        </template>
    </ConfirmDialog>
</template>


<script setup>
import { computed, defineProps, ref } from 'vue';
import { Button, Divider, Image, InputOtp, Panel, Skeleton } from 'primevue';
import * as authApi from "@/api/auth";
import { notifyError, notifySuccess } from '@/utils/notification';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from "primevue/useconfirm";
import { useAuthStore } from "@/stores/authStore";

const authStore = useAuthStore();
const confirmOTP = useConfirm();

const props = defineProps({
    profile: { type: Object, required: true }
});

const otp = ref("");
const resendingCode = ref(false);
const qrCodeUrl = ref("");

const isActive = computed(() => {
    return props.profile.twoFactorEnabled;
});


async function sendVerificationCode(resend = false) {
    try {
        resendingCode.value = true;
        await authApi.generatePasswordResetToken({ email: props.profile.email });
        notifySuccess("Check Your Inbox!", "We've sent a verification code to your email.");
        if (!resend) {
            confirmOTP.require({ group: 'verify_2fa', header: 'Please confirm!' });
        }
    } catch {
        notifyError("Error", "Unable to send verification code. Please try again.");
    } finally {
        resendingCode.value = false;
    }
}

async function verifyCode() {
    try {
        await authApi.verifyResetToken({ email: props.profile.email, code: otp.value });
        otp.value = "";
        if (props.profile.twoFactorEnabled) {
            disableTwoStep();
        } else {
            confirmOTP.close();
            confirmOTP.require({ group: 'register_totp' });
            generateSecretKey();
        }
    } catch {
        notifyError("Invalid Code!", "Please ensure the verification code is correct.");
    }
}

async function disableTwoStep() {
    try {
        const payload = {
            email: props.profile.email,
            status: false
        }
        await authApi.toggleTwoFactor(payload);
        const updated = props.profile;
        updated.twoFactorEnabled = false;
        authStore.updateLocalUser(updated);
        notifySuccess("Success", "Two-step verification has been disabled successfully.");
    } catch {
        notifyError("Oops!", "Unable to disable two-step verification. Please try again.");
    } finally {
        confirmOTP.close();
    }
}

async function activateTwoStep() {
    try {
        const payload = {
            email: props.profile.email,
            status: true
        }
        await authApi.toggleTwoFactor(payload);
        const updated = props.profile;
        updated.twoFactorEnabled = true;
        authStore.updateLocalUser(updated);
        notifySuccess("Success", "Two-step verification has been enabled successfully.");
    } catch {
        notifyError("Oops!", "Unable to disable two-step verification. Please try again.");
    } finally {
        confirmOTP.close();
    }
}

const generateSecretKey = async () => {
    try {
        const payload = {
            userId: props.profile._id,
            role: props.profile.role,
        };

        const res = await authApi.setupAuthenticator(payload);
        qrCodeUrl.value = res.data.data.qrCode;
    } catch (err) {
        console.error("Failed to generate secret:", err.response?.data || err.message);
    }
};
const EnableBtn = computed(() => {
  switch (props.profile.role) {
    case 'student':
      return 'orange-button';
    case 'tutor':
      return 'teal-button';
    case 'institute':
      return 'yellow-button';
    default:
      return 'teal-button';
  }
});
</script>